import { Spinner } from './Components/Spinner';
import { propStyles } from './utils/propStyles';
import get from 'lodash/get';
import { observer, Provider } from 'mobx-react';
import { computed, observable, reaction, action } from 'mobx';
import * as React from 'react';
import { Component, Fragment } from 'react';
import MediaQuery from 'react-responsive';
import styled, { ThemeProvider } from 'styled-components';
import AppLoading from './Components/AppLoading';
import Disclaimers from './Components/Disclaimers';
import DocDone from './Components/DocDone';
import EmptyState from './Components/EmptyState';
import Filters from './Components/Filters';
import FiltersTop from './Components/Filters/Components/FiltersTop';
import ResultsMeta from './Components/ResultsMeta';
import Fund from './Components/Fund';
import FundTypeTabs from './Components/FundTypeTabs';
import Header from './Components/Header';
import LoadingOverlay from './Components/LoadingOverlay';
import LoadMoreButton from './Components/LoadMoreButton';
import ModuleTabs from './Components/ModuleTabs';
import {
    DataStore,
    FilterStore,
    LoadingStore,
    DisclaimerStore,
    AccessibilityStore,
    AssetStore,
    TranslationStore,
    HistoryStore,
    WatchlistStore,
    QueryStore,
    CommentaryStore,
    ScreenSizeStore,
    FeedbackStore,
    EventBusStore,
    PaginationStore,
    InputsStore,
} from './stores';
import { Stores } from './models/Stores';
import withInfiniteScroll from './Components/withInfiniteScroll';
import ModuleDropdown from './Components/ModuleDropdown';
import DocumentModule from './Components/DocumentModule';
import GlobalFontStyles from './GlobalFontStyles';
import GlobalStyles from './GlobalStyles';
import Accessibility from './Components/AccessibilityWrapper';
import ShareableLink from './Components/ShareableLink';
import { AppProps } from './models/AppProps';
import TabRegion from './models/components/TabRegion';
import getTabPanelProps from './utils/getTabPanelProps';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import Feedback from './Components/Feedback';
import { ChildReferences } from './utils/accessibility/ChildReferences';
import HeaderLayout from './Components/HeaderLayout';
import Translate from './Components/Translate';

const SearchHeader = styled<any, any>('header')`
    ${propStyles};
    top: 0;
`;

SearchHeader.defaultProps = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    padding: 'lg',
};

const Wrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

Wrapper.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    marginX: 'auto',
    fontFamily: 'body',
    fontSize: 'sm',
    color: 'body',
};

const Sharing = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

Sharing.defaultProps = {
    display: 'flex',
    flexDirection: 'row-reverse',
};

const Main = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

Main.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    margin: ['0', '0', '0 0 0 240px'],
};

const FundsWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FundsWrapper.defaultProps = {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
};

const HeaderWrapper = styled<any, any>('div')`
    ${propStyles};
    top: ${p => (p.top ? `${p.top}` : 'initial')};
    z-index: ${p => (p.position === 'sticky' ? '13' : undefined)}};
`;

const Container = styled<any, any>('div')`
    ${propStyles};
`;

Container.defaultProps = {
    bgColor: '#fff',
};

const MainContainer = styled<any, any>('section')`
    ${propStyles};
`;

const InfiniteComponent = withInfiniteScroll(Wrapper);

@observer
class App extends Component<AppProps, any> {
    public stores: Stores;
    public dataStore: DataStore;
    public filterStore: FilterStore;
    public accessibilityStore: AccessibilityStore;
    public csvLink: React.RefObject<unknown> = React.createRef();
    @observable public fundGroupChildReferences: ChildReferences<HTMLElement>;

    constructor(props: AppProps) {
        super(props);
        const stores: Stores = {};
        stores.loading = new LoadingStore(stores);
        stores.disclaimer = new DisclaimerStore(stores);
        stores.commentary = new CommentaryStore(stores);
        stores.paginationStore = new PaginationStore(stores);
        stores.data = new DataStore(stores, props);
        stores.screenSize = new ScreenSizeStore(stores);
        stores.translation = new TranslationStore(stores);
        stores.query = new QueryStore(stores);
        stores.feedback = new FeedbackStore(stores.data, stores.query);
        stores.filter = new FilterStore(stores);
        stores.data.stores.filter = stores.filter;
        stores.accessibility = new AccessibilityStore(stores);
        stores.asset = new AssetStore(stores, stores.data);
        stores.history = new HistoryStore(stores.data);
        stores.watchlist = new WatchlistStore(stores);
        stores.eventBusStore = new EventBusStore(stores);
        stores.inputsStore = new InputsStore(stores);
        this.stores = stores;
        stores.data.init(props);
        // bind events
        this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);

        // Initialize or update fundGroupChildReferences instance in reaction to fundGroupCount
        // The fundGroupChildReferences instance needs a count of references to be stored
        reaction(
            () => this.fundGroupCount,
            fundGroupCount => {
                if (!this.fundGroupChildReferences) {
                    this.initializeFundGroupChildReferences();
                    return;
                }
                this.updateFundGroupChildReferences();
            }
        );
    }

    handleLoadMoreClick() {
        const { data } = this.stores;
        data.getFunds(true);
        data.analyticsHelper.logEvent({
            event: 'load_more',
            eventType: 'click',
            context: {},
        });
    }

    @action
    initializeFundGroupChildReferences() {
        this.fundGroupChildReferences = new ChildReferences({
            name: 'FundGroupHeader',
            childCount: this.fundGroupCount,
            debug: false,
        });
    }

    @action
    updateFundGroupChildReferences() {
        this.fundGroupChildReferences.updateChildCount(this.fundGroupCount);
    }

    @computed
    get fundGroupCount(): number {
        return (
            this.stores.data.groupedFunds.length +
            this.stores.data.groupedFunds.reduce((count, fund) => {
                count += fund.subGroupedFunds?.length || 0;
                return count;
            }, 0)
        );
    }

    @computed
    get moduleHeaderStickyConfig() {
        const { stickyConfig } = this.stores.screenSize;
        return stickyConfig?.moduleHeader;
    }

    @computed
    get stickyOffset() {
        const offset = this.moduleHeaderStickyConfig?.offset;
        if (offset || offset === 0) {
            return `${offset}px`;
        }
    }

    @computed
    get stickyPosition() {
        return this.moduleHeaderStickyConfig?.enabled ? 'sticky' : 'static';
    }

    public render() {
        const {
            data,
            filter,
            loading,
            accessibility,
            // asset,
            translation,
            history,
            query,
            screenSize,
            feedback,
        } = this.stores;
        const { mainLandmark } = accessibility;
        const { embedLoadingElement } = this.props;
        return data.isBootstrapped ? (
            <Provider
                dataStore={data}
                filterStore={filter}
                loadingStore={loading}
                translationStore={translation}
                accessibilityStore={accessibility}
                historyStore={history}
                queryStore={query}
                screenSizeStore={screenSize}
                feedbackStore={feedback}
            >
                <ThemeProvider theme={data.theme}>
                    <GlobalFontStyles assetStore={data.assetStore} />
                    <GlobalStyles />
                    <Accessibility>
                        {data.config.showFundTypes && <FundTypeTabs />}
                        <div
                            id={'fundcenter-tooltip'}
                            style={{
                                position: 'relative',
                                // Higher than modal so that it renders correctly
                                zIndex: 1032,
                            }}
                        />
                        <div
                            id={'fundcenter-modal-document-menu'}
                            style={{
                                position: 'relative',
                                zIndex: 1031,
                            }}
                        />
                        <div
                            id={'fundcenter-modal-feedback'}
                            style={{
                                position: 'relative',
                                zIndex: 1033,
                            }}
                        />
                        <div
                            id={'fundcenter-click-layer'}
                            style={{
                                position: 'relative',
                                // No zIndex applied here since the clickLayer handles it's own zIndex through styles.
                            }}
                        />
                        <InfiniteComponent
                            {...getTabPanelProps(
                                data.config.selectedFundTypeId,
                                TabRegion.FundType
                            )}
                            {...data.theme.Wrapper}
                        >
                            <div
                                id={'fundcenter-dropdown'}
                                style={{
                                    position: 'relative',
                                    zIndex: 1032,
                                }}
                            />
                            {loading.global && (
                                <Fragment>
                                    <LoadingOverlay>
                                        <Spinner
                                            color="primary"
                                            iconSize="lg"
                                            {...data.theme.LoadingSpinner}
                                        />
                                    </LoadingOverlay>
                                    <DocDone />
                                </Fragment>
                            )}
                            {!filter.isFiltersTop && <Filters />}
                            <Main
                                {...data.theme.Main}
                                margin={
                                    filter.isFiltersTop ||
                                    (filter &&
                                        !isNullOrEmpty(filter.filters) &&
                                        get(data, 'theme.Main.margin', [
                                            '0',
                                            '0',
                                            '0 0 0 240px',
                                        ]))
                                }
                            >
                                <HeaderWrapper
                                    {...data.theme.HeaderWrapper}
                                    position={this.stickyPosition}
                                    top={this.stickyOffset}
                                >
                                    <Container {...data.theme.HeaderContent}>
                                        {data.config
                                            .hasResponsiveLayoutHeader ? (
                                            <HeaderLayout />
                                        ) : (
                                            <Header />
                                        )}

                                        <Sharing {...data.theme.Sharing}>
                                            {filter.showShareableLink && (
                                                <ShareableLink />
                                            )}
                                        </Sharing>
                                        {filter.isFiltersTop && <FiltersTop />}
                                        {data.config.showModules && (
                                            <MediaQuery
                                                query={`(min-width: ${data.theme.breakpoints.sm})`}
                                            >
                                                {matches =>
                                                    !matches &&
                                                    data.config
                                                        .moduleMobileSelector ===
                                                        'dropdown' ? (
                                                        <ModuleDropdown />
                                                    ) : (
                                                        <ModuleTabs />
                                                    )
                                                }
                                            </MediaQuery>
                                        )}
                                    </Container>
                                </HeaderWrapper>
                                <Container
                                    {...getTabPanelProps(
                                        data.config.currentModuleId,
                                        TabRegion.Module
                                    )}
                                    {...data.theme.ContentWrapper}
                                >
                                    <Container {...data.theme.Content}>
                                        <MainContainer
                                            role="region"
                                            aria-label={mainLandmark}
                                        >
                                            <Disclaimers
                                                disclaimers={
                                                    data.headerDisclaimers
                                                }
                                                theme={{
                                                    ...data.theme,
                                                    DisclaimersList: {
                                                        ...data.theme
                                                            .DisclaimersList,
                                                        ...data.theme
                                                            .HeaderDisclaimersList,
                                                    },
                                                    DisclaimerListItem: {
                                                        ...data.theme
                                                            .DisclaimerListItem,
                                                        ...data.theme
                                                            .HeaderDisclaimerListItem,
                                                    },
                                                    DisclaimerContent: {
                                                        ...data.theme
                                                            .DisclaimerContent,
                                                        ...data.theme
                                                            .HeaderDisclaimerContent,
                                                    },
                                                }}
                                            />
                                            {data.config.isDocumentModule ? (
                                                <DocumentModule />
                                            ) : (
                                                <FundsWrapper>
                                                    {data.groupedFunds.length >
                                                        0 && (
                                                        <Fragment>
                                                            <ResultsMeta
                                                                shareClasses={
                                                                    data
                                                                        .groupedFunds[0]
                                                                        .shareClasses
                                                                }
                                                            />
                                                            {data.groupedFunds.map(
                                                                (
                                                                    fund,
                                                                    index
                                                                ) => (
                                                                    <Fund
                                                                        key={`${data.config.currentModuleId}-${fund.fundName}-${index}`}
                                                                        grouped={
                                                                            fund.grouped
                                                                        }
                                                                        shareClasses={
                                                                            fund.shareClasses
                                                                        }
                                                                        columns={
                                                                            fund.columns
                                                                        }
                                                                        fundGroupChildReferences={
                                                                            this
                                                                                .fundGroupChildReferences
                                                                        }
                                                                        fundGroupIndex={
                                                                            fund.consecutiveIndex
                                                                        }
                                                                        options={{
                                                                            fundName:
                                                                                fund.fundName,
                                                                            isSubFund: false,
                                                                            grouped:
                                                                                fund.grouped,
                                                                        }}
                                                                    >
                                                                        {data.hasSubFunds &&
                                                                            fund.subGroupedFunds.map(
                                                                                (
                                                                                    subGroupedFund,
                                                                                    subIndex
                                                                                ) => (
                                                                                    <Fund
                                                                                        key={`${data.config.currentModuleId}-${subGroupedFund.fundSubName}-${subIndex}`}
                                                                                        shareClasses={
                                                                                            subGroupedFund.shareClasses
                                                                                        }
                                                                                        columns={
                                                                                            fund.columns
                                                                                        }
                                                                                        fundGroupChildReferences={
                                                                                            this
                                                                                                .fundGroupChildReferences
                                                                                        }
                                                                                        fundGroupIndex={
                                                                                            subGroupedFund.consecutiveIndex
                                                                                        }
                                                                                        options={{
                                                                                            grouped:
                                                                                                fund.grouped,
                                                                                            parentFundName:
                                                                                                fund.fundName,
                                                                                            fundName:
                                                                                                subGroupedFund.fundSubName,
                                                                                            isSubFund: true,
                                                                                            subGroupedFund,
                                                                                        }}
                                                                                    />
                                                                                )
                                                                            )}
                                                                    </Fund>
                                                                )
                                                            )}
                                                            <LoadMoreButton
                                                                onClick={
                                                                    this
                                                                        .handleLoadMoreClick
                                                                }
                                                                disabled={
                                                                    data.hasAllFunds
                                                                }
                                                                show={
                                                                    !data.hideLoadMoreButton
                                                                }
                                                                isLoading={
                                                                    loading.fundsMore
                                                                }
                                                                data-metric-id={
                                                                    'loadMoreButton'
                                                                }
                                                            />
                                                        </Fragment>
                                                    )}
                                                    <div aria-live="polite">
                                                        {data.groupedFunds
                                                            .length === 0 &&
                                                            !loading.global && (
                                                                <EmptyState>
                                                                    <Translate>
                                                                        {
                                                                            data
                                                                                .config
                                                                                .noDataText
                                                                        }
                                                                    </Translate>
                                                                </EmptyState>
                                                            )}
                                                    </div>
                                                </FundsWrapper>
                                            )}

                                            <Disclaimers
                                                disclaimers={
                                                    data.footerDisclaimers
                                                }
                                                theme={{
                                                    ...data.theme,
                                                    DisclaimersList: {
                                                        ...data.theme
                                                            .DisclaimersList,
                                                        ...data.theme
                                                            .FooterDisclaimersList,
                                                    },
                                                    DisclaimerListItem: {
                                                        ...data.theme
                                                            .DisclaimerListItem,
                                                        ...data.theme
                                                            .FooterDisclaimerListItem,
                                                    },
                                                    DisclaimerContent: {
                                                        ...data.theme
                                                            .DisclaimerContent,
                                                        ...data.theme
                                                            .FooterDisclaimerContent,
                                                    },
                                                }}
                                            />
                                        </MainContainer>
                                    </Container>
                                </Container>
                            </Main>
                            <Feedback />
                        </InfiniteComponent>
                    </Accessibility>
                </ThemeProvider>
            </Provider>
        ) : (
            <AppLoading embedLoadingElement={embedLoadingElement} />
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx